import {
  Card,
  ComponentState,
  Divider,
  Flex,
  Group,
  Icon,
  Skeleton,
  Space,
  Spinner,
  Tag,
  Text,
  Title,
  Tooltip,
} from "components/common"
import { IconText, Separator, TitleDescription } from "components/custom"
import { LocationMap } from "components/svg"
import { useTranslation } from "react-i18next"
import { joinBytItems, localisedValue } from "utils"
import theme from "utils/themeVars"
import styles from "../index.module.less"
import AmenitiesSection from "./AmenitiesSection"
import PaymentTermsTable from "./PaymentTermsTable"
import PricingSection from "./PricingSection"
import UnitsSoldCard from "./UnitsSoldCard"

const ProjectDetails = ({ project, isInterestedProject, isLoading, error, onRetry }) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Group template="60% 1fr" className="w-100" gap="40px" style={{ alignItems: "start" }}>
        <div>
          <Flex vertical gap="4px" align="start" className="mbe-16">
            <Skeleton height={24} width={250} block />
            <Skeleton height={20} width={220} />
          </Flex>
          <Card Shadow={false} bodyStyle={{ padding: "16px" }} className="mbe-16">
            <Flex wrap={false} gap="8px">
              <Skeleton type="avatar" shape="square" size={"large"} />
              <div>
                <Skeleton height={24} width={300} className="mbe-4" />
                <Skeleton height={20} width={220} />
              </div>
            </Flex>
          </Card>
          <Skeleton height={400} width="100%" block />
        </div>

        <Flex vertical gap="12px">
          <Skeleton height={160} width="100%" block />

          <Skeleton height={198} width="100%" block />
        </Flex>
      </Group>
    )
  }

  if (error) {
    return (
      <ComponentState
        description={error?.message}
        status={error?.status}
        btnLoading={isLoading}
        onClick={onRetry}
        className="w-100"
      />
    )
  }

  if (!project) return null

  const projectName = localisedValue({ object: project, key: "name" })
  const location = localisedValue({ object: project, key: "location" })
  const city = localisedValue({ object: project, key: "city" })
  const developerName = localisedValue({ object: project, key: "developerName" })
  const locationDisplay = joinBytItems([location, city], ", ")

  const openGoogleMaps = () => {
    if (project?.latitude && project?.longitude) {
      window.open(`https://www.google.com/maps?q=${project.latitude},${project.longitude}`, "_blank")
    }
  }
  const noUnitsAvailableCard = () => {
    return (
      <Flex vertical gap="8px" align="center" justify="center" className="w-100" style={{ height: "400px" }}>
        <Icon icon="IoIosCloseCircleOutline" size={30} style={{ color: theme["base-color"] }} />
        <Text strong className="fs16" style={{ color: theme["gray-900"] }}>
          {t("Sold Out")}
        </Text>
        <Text className="gray-800">{t("This Project is completely sold out, try considering other options")}</Text>
      </Flex>
    )
  }
  return (
    <Flex vertical gap={20} className={styles.projectDetailsContainer}>
      <Group
        template={project?.availableUnitsCount ? "60% 1fr" : "initial"}
        className="w-100"
        gap="40px"
        style={{ alignItems: "start" }}
      >
        <Flex align="start" vertical gap={16} className={styles.leftColumn}>
          <div className="mbe-16">
            <TitleDescription
              title={
                <Space className="mbe-4" size={8} wrap={false}>
                  <Title className="mbe-0" level={3}>
                    {projectName}
                  </Title>
                  {isInterestedProject && (
                    <Tooltip title={t("Client's Interested Project")}>
                      <Tag color="#F5C00026" style={{ "--tag-color": "#F5C000" }}>
                        <Icon icon="AiFillStar" size={12} style={{ marginInlineEnd: 4 }} />
                        {t("Interested")}
                      </Tag>
                    </Tooltip>
                  )}
                </Space>
              }
              description={
                <Flex gap="3px">
                  {joinBytItems(
                    [
                      <IconText
                        key="location"
                        gap={4}
                        textType="secondaryLight"
                        icon="MdLocationPin"
                        title={locationDisplay}
                        iconProps={{
                          size: "12px",
                          color: theme["gray800"],
                        }}
                        titleProps={{ style: { fontSize: "12px" } }}
                      />,
                      <IconText
                        key="developer"
                        gap={4}
                        message={t("Developer Name")}
                        textType="secondaryLight"
                        icon="FaHardHat"
                        title={developerName}
                        iconProps={{
                          size: "12px",
                          color: theme["gray800"],
                        }}
                        titleProps={{ style: { fontSize: "12px" } }}
                      />,
                    ],
                    <Icon icon="GoDotFill" size=".7em" color="#D6D6D6" />
                  )}
                </Flex>
              }
              gapSpace={2}
            />
          </div>
          {project?.availableUnitsCount !== 0 ? (
            <>
              <UnitsSoldCard project={project} />
              {project?.pricingAndAvailability?.length > 0 && <Divider />}
              <PricingSection project={project} />

              {!project?.amenities && <Divider />}
              <AmenitiesSection project={project} />
            </>
          ) : (
            noUnitsAvailableCard()
          )}
        </Flex>

        {project?.availableUnitsCount > 0 && (
          <Flex
            style={{ position: "sticky", top: 20 }}
            align="start"
            vertical
            gap={"16px"}
            className={styles.rightColumn}
          >
            <PaymentTermsTable project={project} />
            <Card
              className={`${styles.locationCard} pointer`}
              bordered={false}
              bodyStyle={{ padding: 0 }}
              onClick={openGoogleMaps}
            >
              <Flex gap={"8px"} className={styles.locationCardHeader}>
                <Icon icon="MdOutlineLocationOn" className="text-primary" size={18} style={{ marginTop: "4px" }} />

                <Flex vertical gap="0px">
                  <Text strong style={{ fontSize: "14px", fontWeight: 600 }}>
                    {t("Location")}
                  </Text>
                  <Text type="secondary" className={"fw-normal"} style={{ fontSize: "12px" }}>
                    {t("Click to view the project's location")}
                  </Text>
                </Flex>
              </Flex>
              <Divider style={{ margin: 0 }} />
              <div className={styles.mapContainer}>
                <LocationMap />
              </div>
            </Card>
          </Flex>
        )}
      </Group>
    </Flex>
  )
}

export default ProjectDetails
